<template>
  <v-menu
    v-if="!readOnly"
    ref="menu"
    v-model="menu"
    :close-on-content-click="isYearPicker"
    transition="scale-transition"
    offset-x
    min-width="290px"
    fixed
  >
    <template v-slot:activator="{ on: { click: openDatePicker } }">
      <ValidationField :uuid="uuid" :propPath="propPath" :formRef="formRef">
        <template v-slot:default="{ error, deleteServerError }">
          <b-text-field
            class="w-date-field"
            ref="textField"
            v-bind="$props"
            clearable
            append-icon="mdi-calendar"
            @click:append="openDatePicker"
            :errorMessages="error ? error : errorMessages"
            :value="formattedDate"
            @change="formattedDate = $event"
            @keyup="deleteServerError"
            @input="deleteServerError"
          />
        </template>
      </ValidationField>
    </template>
    <v-date-picker
      ref="picker"
      v-model="internalValue"
      @change="$refs.menu.save($event)"
      no-title
      reactive
      scrollable
      :min="min"
      :max="max"
      locale="de"
      :type="month ? 'month' : undefined"
    />
  </v-menu>
  <b-read-only-text-field v-else v-bind="$props" :value="formattedDate" />
</template>

<script>
import { VTextField } from 'vuetify/lib';
import ValidationField from '@/components/base_components/ValidationField';

export default {
  name: 'BDateField',
  components: { ValidationField },
  props: {
    readOnly: Boolean,
    inlineReadOnly: { type: Boolean, default: false },
    startPicker: {
      type: String,
      validator: value => ['YEAR', 'MONTH', 'DATE'].includes(value),
      default: undefined
    },
    isYearPicker: Boolean,
    month: Boolean,
    uuid: String,
    propPath: String,
    formRef: String,
    min: String,
    max: String,
    ...VTextField.options.props
  },
  data() {
    return {
      lazyValue: this.value,
      menu: false
    };
  },
  computed: {
    formattedDate: {
      get() {
        return this.formatISOString(this.internalValue);
      },
      set(newValue) {
        var inputValue =
          this.internalValue == undefined && newValue == undefined
            ? ''
            : newValue;
        this.internalValue =
          inputValue == undefined ? undefined : this.parseDate(inputValue);
        this.$refs.textField.lazyValue = this.formattedDate;
      }
    },
    internalValue: {
      get() {
        return this.lazyValue;
      },
      set(val) {
        //The day has to be added manually, if set by using the date picker of type 'month'
        if (val != undefined && val.length == 7) {
          val = val + '-01';
        }
        if (this.isYearPicker && val) {
          val = val.substring(0, 4);
        }
        if (val != this.lazyValue) {
          this.lazyValue = val;
          this.$emit('input', val);
        }
      }
    }
  },
  methods: {
    parseDate(dateString) {
      //By first formatting the input with a library function, we essentially reuse its date parsing functionality.
      //Whatever defined value this method outputs is a valid date.
      var formattedDateString = this.formatISOString(
        this.parseToISO(dateString)
      );

      if (formattedDateString == undefined) return undefined;

      var isoDate = this.parseToISO(formattedDateString);

      return isoDate.match(/^\d{4}-\d{2}-\d{2}$/) ? isoDate : undefined;
    },
    parseToISO(dateString) {
      var cleanDate = dateString.replace(/\D/g, '');

      if (this.month) {
        var day = '01';
        var month = cleanDate.substring(0, 2);
        var year = cleanDate.substring(2);
      } else if (this.isYearPicker) {
        day = '01';
        month = '01';
        year = cleanDate.substring(0, 4);
      } else {
        if (this.$i18n) {
          day =
            this.$i18n.locale === 'en'
              ? cleanDate.substring(2, 4)
              : cleanDate.substring(0, 2);
          month =
            this.$i18n.locale === 'en'
              ? cleanDate.substring(0, 2)
              : cleanDate.substring(2, 4);
        }
        year = cleanDate.substring(4);
      }

      var today = new Date();

      if (day === undefined || day.length == 0) {
        day = today.getDate();
      }
      if (month === undefined || month.length == 0) {
        month = today.getMonth() + 1; //returned month ranges from 0 to 11
      }
      if (year.length == 0) year = today.getFullYear();
      if (year.length == 2) year = '20' + year;

      return `${year}-${month}-${day}`;
    },
    formatISOString(dateString) {
      if (dateString == undefined) return undefined;

      var parts = dateString.split(/\D/g);
      if (parts.length > 1 && !this.isYearPicker) {
        var month = parts[1].slice(0) - 1; //month ranges from 0 to 11
        if (this.month) {
          var dateObj = new Date(parts[0], month, 1, 0, 0, 0, 0);
          return dateObj.toLocaleDateString(
            this.$i18n ? this.$i18n.locale : 'de',
            {
              month: '2-digit',
              year: 'numeric'
            }
          );
        } else {
          dateObj = new Date(parts[0], month, parts[2], 0, 0, 0, 0);
          return dateObj.toLocaleDateString(
            this.$i18n ? this.$i18n.locale : 'de',
            {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            }
          );
        }
      } else {
        dateObj = new Date(parts[0], 1, 1, 0, 0, 0, 0);
        return dateObj.toLocaleDateString(
          this.$i18n ? this.$i18n.locale : 'de',
          {
            year: 'numeric'
          }
        );
      }
    },
    focus() {
      this.$refs.textField.focus();
    }
  },
  watch: {
    value(val) {
      if (Array.isArray(val)) {
        val = val.toString();
      }
      this.lazyValue = val;
    },
    menu(val) {
      val &&
        this.startPicker &&
        setTimeout(() => (this.$refs.picker.activePicker = this.startPicker));
    },
    internalValue(val) {
      this.$emit('change', val);
    },
  }
};
</script>

<style lang="scss">
.b-date-field {
  & .v-input__icon--append {
    & > button {
      color: var(--v-primary-base);
    }
  }

  & .v-text-field__slot {
    min-width: 80px;
  }

  & legend {
    transition: none;
  }
}
</style>
